import React, { useCallback, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Box, Flex } from '@qga/roo-ui/components';

//Legacy
import { useScrollIntoView } from '@shared/v2/components/InView';
import usePackageSearch from '@shared/v2/hooks/usePackageSearch';
import useSearchQuery from '@shared/v2/hooks/useSearchQuery';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Markdown from '@/components/ConditionsMarkdown';
import Text from '@/components/Text';
// Hooks
import useDestination from '@/hooks/useDestination';
import Modal from '@/v2/components/layout/Modal';
import { useRoomOptionsData } from '@/v2/components/pages/PackageViewV2/contexts/RoomOptionsContext';
import { useSearchPanelControls } from '@/v2/components/pages/PackageViewV2/contexts/SearchPanelContext';
import {
  DateRangeInput,
  Dates,
  TravellersInput,
} from '@/v2/components/ui/Input';
import { useCampaign } from '@/v2/contexts/campaign';
// Context hooks
import { useDestination as v2UseDestination } from '@/v2/contexts/destination';
import creditVouchersTerms from '@/v2/markdown/creditVouchersTerms.md';
// Types
import { Travellers } from '@/v2/services/sanity/types/api/response';

const BoldText = styled(Text)`
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0.1px;
`;

const MobileSearchPanelModal = ({ onClose }: { onClose: () => void }) => {
  const {
    isCalendarOpen,
    openCalendar,
    closeCalendar,
    isTravellersOpen,
    closeTravellers,
    openTravellers,
    calendarRoomId,
    setCalendarRoomId,
    closeMobileSearchPanelModal,
  } = useSearchPanelControls();
  const scrollIntoView = useScrollIntoView();
  const { searchQuery } = useSearchQuery();

  const [showErrors, setShowErrors] = useState(false);

  const { roomOptions } = useRoomOptionsData() || {};

  const campaign = useCampaign();

  const d = useDestination();
  const dV2 = v2UseDestination();
  const destination = d || dV2;

  const {
    data: { departureDate, returnDate, originCode, adults, children, infants },
    handleChangeData,
    handleSearch,
    error,
  } = usePackageSearch();

  const dates = { departureDate, returnDate };
  const travellers = { adults, children, infants };

  const blackoutDates = useMemo(
    () => destination && originCode && destination.blackoutDates[originCode],
    [destination, originCode],
  );

  const minimumNights = useMemo(
    () => destination && destination.searchConfig.minimumNights,
    [destination],
  );

  const disabled = !originCode;

  const handleDatesSubmit = useCallback(
    (selectedRoomId?: string) => {
      const noErrors = Object.values(error).every((v) => !v);

      if (noErrors) {
        setCalendarRoomId(selectedRoomId);
        handleSearch({ room: selectedRoomId });
      } else {
        setShowErrors(true);
      }
    },
    [error, setCalendarRoomId, handleSearch, setShowErrors],
  );

  const handleDatesChange = useMemo(
    () => (dates?: Dates) => {
      setShowErrors(false);

      const departing =
        dates?.departureDate instanceof Date
          ? dates?.departureDate.toString()
          : dates?.departureDate;
      const returning =
        dates?.returnDate instanceof Date
          ? dates?.returnDate.toString()
          : dates?.returnDate;

      handleChangeData({
        departureDate: departing,
        returnDate: returning,
      });
    },
    [handleChangeData],
  );

  const handleTravellersChange = useMemo(
    () => (travellers: Travellers) => {
      setShowErrors(false);

      const { adults, children, infants } = travellers;

      handleChangeData({ adults, children, infants });
    },
    [handleChangeData],
  );

  const roomTypeOptions = roomOptions?.main?.map((roomOption) => ({
    value: roomOption.roomType.id,
    text: roomOption.roomType.name,
  }));

  return (
    <Modal size="lg" onClose={onClose}>
      <Modal.Header hideBorderBottom>&nbsp;</Modal.Header>
      <Modal.Body pt="2" px={['4', null, '5']} pb={['4', null, '6']}>
        <Flex
          flexDirection="column"
          justifyContent="space-between"
          height="100%"
        >
          <Flex flexDirection="column" gap="4">
            <DateRangeInput
              isOpen={isCalendarOpen}
              onClose={closeCalendar}
              onOpen={openCalendar}
              dates={dates}
              blackoutDates={blackoutDates}
              minimumNights={minimumNights}
              onDataChange={handleDatesChange}
              onDataSubmit={handleDatesSubmit}
              campaign={campaign}
              error={showErrors ? error : undefined}
              disabled={disabled}
              roomId={calendarRoomId || searchQuery?.room}
              roomTypes={roomTypeOptions}
            />
            <TravellersInput
              position="BOTTOM_WITH_OFFSET"
              isOpen={isTravellersOpen}
              onOpen={openTravellers}
              onClose={closeTravellers}
              travellers={travellers}
              onDataChange={handleTravellersChange}
              error={showErrors ? error : undefined}
              disabled={disabled}
              fullWidthDropdown
            />
            <Alert.Info icon={{ name: 'info', color: 'brand.secondary' }}>
              <BoldText>COVID-19 requirements</BoldText>
              <Markdown
                markdown={creditVouchersTerms}
                paragraphMarginBottom={0}
              />
            </Alert.Info>
          </Flex>
          <Button
            onClick={() => {
              closeTravellers();
              closeMobileSearchPanelModal();
              closeCalendar();
              scrollIntoView('room');
            }}
            width="100%"
          >
            <Box py="2" fontSize="md" color="white">
              Check availability
            </Box>
          </Button>
        </Flex>
      </Modal.Body>
    </Modal>
  );
};

export default MobileSearchPanelModal;
